/**
 * Diálogo de exportación SUENLACE por archivos con distinta fecha contable.
 *
 * Flujo:
 *   1. Al abrirse, crea un único archivo con TODAS las facturas y la fecha contable
 *      sugerida (la del campo global del Libro de IVA). Comportamiento idéntico
 *      al exportador clásico si el usuario no toca nada.
 *   2. "+ Nuevo archivo" crea Archivo 2, Archivo 3… cada uno con su propia fecha
 *      contable. El usuario reasigna facturas usando el desplegable por fila o
 *      el botón "Asignar todas".
 *   3. Al confirmar:
 *      - Valida cada archivo (PreExportValidator.collectValidationIssues).
 *      - Genera un SUENLACE por archivo (buildA3SuenlaceFile).
 *      - Empaqueta los .dat + un único Excel del Libro de IVA en un ZIP.
 *
 * La asignación es efímera: vive solo dentro del diálogo. No se persiste.
 */

import { useMemo, useState, useEffect, useRef } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Plus, Trash2, X, MoveRight, GripVertical } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import JSZip from "jszip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { ProcessedFile } from "./InvoiceList";
import type { CompanyMaster } from "@/lib/supplierMaster";
import { collectValidationIssues } from "./PreExportValidator";
import { buildA3SuenlaceFile } from "@/lib/a3ecoExport";
import { useCompanyExportSettings } from "@/hooks/useCompanyExportSettings";
import { buildA3RowsForFiles, buildLibroIvaExcelBlob } from "@/lib/exportBatches";
import { computeSeriesNumbers } from "@/lib/invoiceSeries";
import { sanitizeFileName } from "@/lib/pdfDownload";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";
import { useToast } from "@/hooks/use-toast";

interface Archivo {
  id: string;
  accountingDate: string;
}

export interface ExportBatchesResult {
  /** Total de líneas SUENLACE generadas (sumadas de todos los archivos). */
  lineCount: number;
  /** Total de facturas exportadas. */
  invoiceCount: number;
  /** Avisos no críticos devueltos por buildA3SuenlaceFile. */
  warnings: string[];
}

interface ExportBatchesDialogProps {
  open: boolean;
  onClose: () => void;
  files: ProcessedFile[];
  appliedCompany: CompanyMaster | null;
  prefix: string;
  startNumber: number;
  /** Fecha contable global ya escrita por el usuario en la cabecera. */
  defaultAccountingDate: string;
  /** Se invoca tras una exportación exitosa para que VatBook lance el volcado al histórico. */
  onExported?: (result: ExportBatchesResult) => void;
}

const newArchivoId = () => `archivo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const fmt2 = (n: number) => formatNumber(n);

const ExportBatchesDialog = ({
  open,
  onClose,
  files,
  appliedCompany,
  prefix,
  startNumber,
  defaultAccountingDate,
  onExported,
}: ExportBatchesDialogProps) => {
  const { toast } = useToast();
  const orgExportSettings = useCompanyExportSettings(appliedCompany?.organization_id ?? null);
  /**
   * Cuando la asesoría contabiliza con la fecha de cada factura, la fecha contable
   * del fichero deja de tener sentido: ni se pide ni se valida.
   */
  const useInvoiceDate = orgExportSettings.useInvoiceDateAsEntryDate;

  const seriesNumbersById = useMemo(() => computeSeriesNumbers(files.filter(Boolean), prefix, startNumber), [files, prefix, startNumber]);
  const [archivos, setArchivos] = useState<Archivo[]>([]);
  /** fileId → archivo.id */
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dragOverArchivoId, setDragOverArchivoId] = useState<string | null>(null);
  /** Archivo actualmente visible en el listado inferior. Al mover facturas a otro archivo, desaparecen de aquí. */
  const [viewArchivoId, setViewArchivoId] = useState<string>("");
  const lastClickedIdRef = useRef<string | null>(null);
  /** Drag-select con ratón sobre las filas: pulsar + arrastrar selecciona en bloque sin Shift. */
  const dragSelectModeRef = useRef<"add" | "remove" | null>(null);

  // Reset al abrir: 1 solo archivo con todas las facturas
  useEffect(() => {
    if (!open) return;
    const firstId = newArchivoId();
    setArchivos([{ id: firstId, accountingDate: defaultAccountingDate || "" }]);
    const map: Record<string, string> = {};
    for (const f of files) map[f.id] = firstId;
    setAssignments(map);
    setSelectedIds(new Set());
    setDragOverArchivoId(null);
    setViewArchivoId(firstId);
    lastClickedIdRef.current = null;
    dragSelectModeRef.current = null;
    setIsExporting(false);
  }, [open, defaultAccountingDate, files]);

  // Cualquier mouseup global termina el drag-select.
  useEffect(() => {
    const stop = () => { dragSelectModeRef.current = null; };
    window.addEventListener("mouseup", stop);
    window.addEventListener("dragend", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("dragend", stop);
    };
  }, []);

  const addArchivo = () => {
    const id = newArchivoId();
    setArchivos((prev) => [...prev, { id, accountingDate: "" }]);
    setViewArchivoId(id);
  };

  const removeArchivo = (id: string) => {
    if (archivos.length <= 1) return;
    const fallback = archivos.find((b) => b.id !== id)!.id;
    setArchivos((prev) => prev.filter((b) => b.id !== id));
    setAssignments((prev) => {
      const next: Record<string, string> = {};
      for (const [fileId, archivoId] of Object.entries(prev)) {
        next[fileId] = archivoId === id ? fallback : archivoId;
      }
      return next;
    });
    setViewArchivoId((cur) => (cur === id ? fallback : cur));
  };

  const setArchivoDate = (id: string, value: string) => {
    setArchivos((prev) => prev.map((b) => (b.id === id ? { ...b, accountingDate: value } : b)));
  };

  const assignAllTo = (archivoId: string) => {
    const map: Record<string, string> = {};
    for (const f of files) map[f.id] = archivoId;
    setAssignments(map);
    setSelectedIds(new Set());
  };

  // Orden de visualización: facturas por fecha asc para que sea fácil asignar.
  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [files],
  );

  // Solo mostramos las facturas del archivo activo en el listado inferior.
  const visibleFiles = useMemo(
    () => (viewArchivoId ? sortedFiles.filter((f) => (assignments[f.id] || "") === viewArchivoId) : sortedFiles),
    [sortedFiles, assignments, viewArchivoId],
  );

  const toggleSelect = (fileId: string, ev?: React.MouseEvent) => {
    if (ev?.shiftKey && lastClickedIdRef.current && lastClickedIdRef.current !== fileId) {
      const ids = visibleFiles.map((f) => f.id);
      const a = ids.indexOf(lastClickedIdRef.current);
      const b = ids.indexOf(fileId);
      if (a !== -1 && b !== -1) {
        const [from, to] = a < b ? [a, b] : [b, a];
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) next.add(ids[i]);
          return next;
        });
        lastClickedIdRef.current = fileId;
        return;
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
    lastClickedIdRef.current = fileId;
  };

  /** Inicia drag-select con ratón: pulsar+arrastrar sobre filas selecciona en bloque. */
  const startDragSelect = (fileId: string, ev: React.MouseEvent) => {
    if (ev.button !== 0 || ev.shiftKey) return;
    // Si el mousedown está sobre un control interactivo (checkbox, grip, botón), no interferimos.
    const target = ev.target as HTMLElement;
    if (target.closest("[data-drag-handle], [role='checkbox'], button, input, select, a")) return;
    const willAdd = !selectedIds.has(fileId);
    dragSelectModeRef.current = willAdd ? "add" : "remove";
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (willAdd) next.add(fileId);
      else next.delete(fileId);
      return next;
    });
    lastClickedIdRef.current = fileId;
  };

  const extendDragSelect = (fileId: string) => {
    if (!dragSelectModeRef.current) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (dragSelectModeRef.current === "add") next.add(fileId);
      else next.delete(fileId);
      return next;
    });
  };

  const allVisibleSelected = visibleFiles.length > 0 && visibleFiles.every((f) => selectedIds.has(f.id));
  const someVisibleSelected = !allVisibleSelected && visibleFiles.some((f) => selectedIds.has(f.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const f of visibleFiles) next.delete(f.id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const f of visibleFiles) next.add(f.id);
        return next;
      });
    }
  };

  const moveIdsTo = (ids: string[], archivoId: string) => {
    if (ids.length === 0) return;
    setAssignments((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = archivoId;
      return next;
    });
  };

  const moveSelectionTo = (archivoId: string) => {
    moveIdsTo(Array.from(selectedIds), archivoId);
    setSelectedIds(new Set());
  };

  const moveSelectionToNewArchivo = () => {
    if (selectedIds.size === 0) return;
    const id = newArchivoId();
    setArchivos((prev) => [...prev, { id, accountingDate: "" }]);
    moveIdsTo(Array.from(selectedIds), id);
    setSelectedIds(new Set());
  };

  const handleDragStart = (ev: React.DragEvent, fileId: string) => {
    const payload = selectedIds.has(fileId) && selectedIds.size > 1
      ? Array.from(selectedIds)
      : [fileId];
    ev.dataTransfer.effectAllowed = "move";
    try { ev.dataTransfer.setData("application/json", JSON.stringify(payload)); } catch { /* noop */ }
    (ev.currentTarget as HTMLElement).dataset.dragPayload = JSON.stringify(payload);
  };

  const handleArchivoDrop = (ev: React.DragEvent, archivoId: string) => {
    ev.preventDefault();
    setDragOverArchivoId(null);
    let ids: string[] = [];
    try {
      const raw = ev.dataTransfer.getData("application/json");
      if (raw) ids = JSON.parse(raw);
    } catch { /* noop */ }
    if (ids.length === 0 && selectedIds.size > 0) ids = Array.from(selectedIds);
    moveIdsTo(ids, archivoId);
    if (selectedIds.size > 0) setSelectedIds(new Set());
  };

  // Agrupamos facturas por archivo para mostrar resumen y validar
  const filesByArchivo = useMemo(() => {
    const out: Record<string, ProcessedFile[]> = {};
    for (const b of archivos) out[b.id] = [];
    for (const f of files) {
      const aid = assignments[f.id];
      if (aid && out[aid]) out[aid].push(f);
    }
    return out;
  }, [files, archivos, assignments]);

  const archivoSummaries = useMemo(() => {
    return archivos.map((b, idx) => {
      const list = filesByArchivo[b.id] || [];
      const totalBase = list.reduce((s, f) => {
        const sum = (f.vatBreakdown ?? []).reduce((a, l) => a + parseNumericLike(l.taxableBase), 0);
        return s + sum;
      }, 0);
      const totalAmount = list.reduce((s, f) => s + parseNumericLike(f.totalAmount ?? 0), 0);
      return { archivo: b, index: idx, count: list.length, totalBase, totalAmount };
    });
  }, [archivos, filesByArchivo]);

  // Errores de validación: cada archivo sin fecha o con issues críticas bloquea.
  const validation = useMemo(() => {
    const perArchivo: { archivoId: string; issues: ReturnType<typeof collectValidationIssues> }[] = [];
    const globalErrors: string[] = [];
    for (const b of archivos) {
      if (!useInvoiceDate && !b.accountingDate) {
        globalErrors.push(`El Archivo ${archivos.indexOf(b) + 1} no tiene fecha contable.`);
      }
      const list = filesByArchivo[b.id] || [];
      perArchivo.push({ archivoId: b.id, issues: collectValidationIssues(list, appliedCompany) });
    }
    const emptyArchivos = archivos.filter((b) => (filesByArchivo[b.id] || []).length === 0);
    if (emptyArchivos.length > 0 && archivos.length > 1) {
      globalErrors.push(
        `Hay ${emptyArchivos.length} archivo(s) sin facturas asignadas. Asigna alguna o elimínalos.`,
      );
    }
    const criticalCount =
      globalErrors.length +
      perArchivo.reduce((s, p) => s + p.issues.filter((i) => i.severity === "error").length, 0);
    return { perArchivo, globalErrors, criticalCount };
  }, [archivos, filesByArchivo, appliedCompany, useInvoiceDate]);

  const handleExport = async () => {
    if (!appliedCompany) return;
    const code = (appliedCompany.a3_company_code || "").replace(/\D/g, "");
    if (!code) {
      toast({
        title: "Falta código de empresa A3",
        description: `Configura el "Código A3" para "${appliedCompany.name}".`,
        variant: "destructive",
      });
      return;
    }
    if (validation.criticalCount > 0) return;

    setIsExporting(true);
    try {
      const zip = new JSZip();
      let totalLines = 0;
      let totalInvoices = 0;
      const allWarnings: string[] = [];
      const empresaSlug = sanitizeFileName(appliedCompany.name || "empresa")
        .replace(/\s+/g, "_")
        .slice(0, 40);

      // 1) Un SUENLACE por archivo
      for (let i = 0; i < archivos.length; i++) {
        const b = archivos[i];
        const list = filesByArchivo[b.id] || [];
        if (list.length === 0) continue;
        const a3Rows = buildA3RowsForFiles(
          list,
          files,
          prefix,
          startNumber,
          useInvoiceDate ? "" : b.accountingDate,
        );
        const result = buildA3SuenlaceFile(a3Rows, {
          companyCode: code,
          startEntryNumber: startNumber,
          useInvoiceDateAsEntryDate: orgExportSettings.useInvoiceDateAsEntryDate,
          omitVatLedgerNumber: orgExportSettings.omitVatLedgerNumber,
        });
        if (result.lineCount === 0) continue;
        totalLines += result.lineCount;
        totalInvoices += result.invoiceCount;
        if (result.warnings.length > 0) {
          allWarnings.push(...result.warnings.map((w) => `Archivo ${i + 1}: ${w}`));
        }
        const dateTag = (
          useInvoiceDate ? new Date().toISOString().slice(0, 10) : b.accountingDate
        ).replace(/-/g, "");
        const fileName =
          useInvoiceDate && archivos.length > 1
            ? `SUENLACE_${empresaSlug}_${dateTag}_${i + 1}.dat`
            : `SUENLACE_${empresaSlug}_${dateTag}.dat`;
        // Copia para evitar problemas de detached buffer
        // Copia los bytes en un Uint8Array propio para evitar SharedArrayBuffer issues.
        const copy = new Uint8Array(result.bytes.byteLength);
        copy.set(result.bytes);
        zip.file(fileName, copy);
      }

      if (totalLines === 0) {
        toast({
          title: "Sin datos para exportar",
          description: "Ningún archivo ha generado líneas SUENLACE.",
          variant: "destructive",
        });
        setIsExporting(false);
        return;
      }

      // 2) Un único Excel con todas las facturas y su fecha contable
      const accountingDateByFile = new Map<string, string>();
      for (const [fileId, archivoId] of Object.entries(assignments)) {
        const b = archivos.find((x) => x.id === archivoId);
        if (!b) continue;
        if (useInvoiceDate) {
          const f = files.find((x) => x.id === fileId);
          accountingDateByFile.set(fileId, f?.date || "");
        } else {
          accountingDateByFile.set(fileId, b.accountingDate);
        }
      }
      const excelBlob = await buildLibroIvaExcelBlob(
        files,
        prefix,
        startNumber,
        accountingDateByFile,
      );
      const excelName = `LibroIVA_${empresaSlug}.xlsx`;
      zip.file(excelName, await excelBlob.arrayBuffer());

      // 3) Descargamos el ZIP
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      const zipName = `SUENLACE_${empresaSlug}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.zip`;
      window.setTimeout(() => {
        try {
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = sanitizeFileName(zipName);
          a.rel = "noopener";
          a.click();
          window.setTimeout(() => {
            try {
              URL.revokeObjectURL(url);
            } catch {
              /* ignore */
            }
          }, 120000);
        } catch (e) {
          console.error("ZIP download error:", e);
        }
      }, 0);

      toast({
        title: "SUENLACE generado",
        description:
          archivos.length === 1
            ? `${totalInvoices} facturas · ${totalLines} líneas`
            : `${archivos.length} archivos · ${totalInvoices} facturas · ${totalLines} líneas`,
      });

      onExported?.({
        lineCount: totalLines,
        invoiceCount: totalInvoices,
        warnings: allWarnings,
      });
      onClose();
    } catch (e) {
      console.error("Export archivos error:", e);
      toast({
        title: "Error al generar la exportación",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const blocked = validation.criticalCount > 0 || isExporting;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isExporting) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Exportar SUENLACE</DialogTitle>
          <DialogDescription>
            {useInvoiceDate
              ? "Cada factura se contabiliza con su propia fecha. Puedes agrupar las facturas en varios archivos; se generará un fichero SUENLACE por cada archivo y un único Excel con todas las facturas, todo dentro de un mismo ZIP."
              : "Agrupa las facturas en archivos con distinta fecha contable. Se generará un fichero SUENLACE por cada archivo y un único Excel con todas las facturas, todo dentro de un mismo ZIP."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-1">

        {/* Cabecera de archivos */}
        <div className="space-y-2 border-b pb-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Archivos ({archivos.length})
            </h3>
            <Button variant="outline" size="sm" onClick={addArchivo} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Nuevo archivo
            </Button>
          </div>

          <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1">
            {archivoSummaries.map(({ archivo, index, count, totalBase, totalAmount }) => {
              const isDropTarget = dragOverArchivoId === archivo.id;
              return (
              <div
                key={archivo.id}
                onClick={() => setViewArchivoId(archivo.id)}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverArchivoId(archivo.id); }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverArchivoId(null); }}
                onDrop={(e) => handleArchivoDrop(e, archivo.id)}
                className={
                  "flex flex-wrap items-center gap-3 rounded-md border bg-card p-3 transition-colors cursor-pointer " +
                  (isDropTarget ? "border-primary ring-2 ring-primary/30 bg-primary/5 " : "") +
                  (viewArchivoId === archivo.id ? "ring-2 ring-primary/40 border-primary" : "")
                }
                title="Click para ver las facturas de este archivo"
              >
                <Badge variant={viewArchivoId === archivo.id ? "default" : "secondary"} className="shrink-0">
                  Archivo {index + 1}
                </Badge>
                {useInvoiceDate ? (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Fecha de cada factura
                  </span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Label
                      htmlFor={`archivo-date-${archivo.id}`}
                      className="text-xs text-muted-foreground whitespace-nowrap"
                    >
                      Fecha contable
                    </Label>
                    <Input
                      id={`archivo-date-${archivo.id}`}
                      type="date"
                      value={archivo.accountingDate}
                      onChange={(e) => setArchivoDate(archivo.id, e.target.value)}
                      className="h-8 w-[150px] text-xs"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    <strong className="text-foreground">{count}</strong>{" "}
                    factura{count === 1 ? "" : "s"}
                  </span>
                  <span>·</span>
                  <span>
                    Base: <strong className="text-foreground">{fmt2(totalBase)} €</strong>
                  </span>
                  <span>·</span>
                  <span>
                    Total: <strong className="text-foreground">{fmt2(totalAmount)} €</strong>
                  </span>
                </div>
                {isDropTarget && (
                  <span className="text-xs font-medium text-primary">Suelta aquí para asignar</span>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {selectedIds.size > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => moveSelectionTo(archivo.id)}
                      title={`Mover las ${selectedIds.size} seleccionadas a este archivo`}
                    >
                      <MoveRight className="h-3.5 w-3.5" />
                      Mover {selectedIds.size}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => assignAllTo(archivo.id)}
                    title="Asigna todas las facturas a este archivo"
                  >
                    Asignar todas
                  </Button>
                  {archivos.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={() => removeArchivo(archivo.id)}
                      title="Eliminar archivo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Barra de selección y cabecera de lista */}
        <div className="flex items-center gap-3 border rounded-md px-3 py-2 bg-muted/30 text-xs">
          <Checkbox
            checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
            onCheckedChange={toggleSelectAll}
            aria-label="Seleccionar todas las visibles"
          />
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              Viendo: Archivo {Math.max(1, archivos.findIndex((b) => b.id === viewArchivoId) + 1)}
              {" · "}
              {visibleFiles.length} factura{visibleFiles.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground">
              {selectedIds.size > 0
                ? `${selectedIds.size} seleccionada${selectedIds.size === 1 ? "" : "s"}`
                : "Click+arrastra sobre las filas para seleccionar en bloque · Shift+Click para rango · Arrastra el handle a un archivo"}
            </span>
          </div>
          {selectedIds.size > 0 && (
            <div className="ml-auto flex flex-wrap items-center gap-1">
              <span className="text-muted-foreground">Mover a:</span>
              {archivos.map((b, idx) => (
                <Button
                  key={b.id}
                  size="sm"
                  variant={viewArchivoId === b.id ? "ghost" : "outline"}
                  disabled={viewArchivoId === b.id}
                  className="h-7 text-xs"
                  onClick={() => moveSelectionTo(b.id)}
                  title={viewArchivoId === b.id ? "Ya están en este archivo" : `Mover a Archivo ${idx + 1}`}
                >
                  Archivo {idx + 1}
                  {b.accountingDate ? ` · ${b.accountingDate}` : ""}
                </Button>
              ))}
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={moveSelectionToNewArchivo}>
                <Plus className="h-3 w-3" /> Nuevo archivo
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedIds(new Set())}>
                Limpiar
              </Button>
            </div>
          )}
        </div>

        {/* Lista de facturas del archivo activo */}
        <div className="flex-1 min-h-[200px] overflow-y-auto border rounded-md p-2">
          {visibleFiles.length === 0 ? (
            <div className="flex h-full min-h-[160px] items-center justify-center text-xs text-muted-foreground text-center px-6">
              Este archivo no tiene facturas asignadas. Selecciona otro archivo arriba o arrastra facturas hasta aquí.
            </div>
          ) : (
          <div className="space-y-1">
            {visibleFiles.map((f) => {
              const total = parseNumericLike(f.totalAmount ?? 0);
              const vatSeries = seriesNumbersById.get(f.id) ?? "";
              const selected = selectedIds.has(f.id);
              return (
                <div
                  key={f.id}
                  onMouseDown={(e) => startDragSelect(f.id, e)}
                  onMouseEnter={() => extendDragSelect(f.id)}
                  className={
                    "flex items-center gap-3 rounded-md border bg-card px-3 py-2 text-xs select-none transition-colors " +
                    (selected ? "bg-accent/40 border-primary/50" : "")
                  }
                >
                  <Checkbox
                    checked={selected}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(f.id, e as unknown as React.MouseEvent); }}
                    aria-label="Seleccionar factura"
                  />
                  <span
                    data-drag-handle
                    draggable
                    onDragStart={(e) => handleDragStart(e, f.id)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="cursor-grab active:cursor-grabbing text-muted-foreground shrink-0 p-0.5 hover:text-foreground"
                    title="Arrastra a un archivo para mover"
                  >
                    <GripVertical className="h-3.5 w-3.5" />
                  </span>
                  <span className="w-20 shrink-0 font-mono text-muted-foreground">
                    {f.date || "—"}
                  </span>
                  <Badge variant="outline" className="w-20 shrink-0 justify-center font-mono text-[10px]">
                    {vatSeries || "—"}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {f.supplier || "Proveedor s/n"}
                  </span>
                  <span className="w-28 shrink-0 truncate font-mono text-muted-foreground">
                    {f.invoiceNumber || "(s/n)"}
                  </span>
                  <span className="w-24 shrink-0 text-right font-mono">
                    {fmt2(total)} €
                  </span>
                </div>
              );
            })}
          </div>
          )}
        </div>





        {/* Validación */}
        {(validation.globalErrors.length > 0 || validation.criticalCount > 0) && (
          <div className="space-y-1 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs">
            <div className="flex items-center gap-2 font-medium text-destructive">
              <AlertCircle className="h-4 w-4" />
              {validation.criticalCount} problema{validation.criticalCount === 1 ? "" : "s"} a resolver
            </div>
            {validation.globalErrors.map((e, i) => (
              <p key={i} className="ml-6 text-foreground">• {e}</p>
            ))}
            {validation.perArchivo.map(({ archivoId, issues }, idx) => {
              const critical = issues.filter((i) => i.severity === "error");
              if (critical.length === 0) return null;
              return (
                <div key={archivoId} className="ml-6">
                  <p className="font-medium">Archivo {idx + 1}:</p>
                  <ul className="ml-3 list-disc">
                    {critical.slice(0, 5).map((it) => (
                      <li key={it.id}>
                        <span className="text-muted-foreground">{it.invoiceLabel}</span> — {it.message}
                      </li>
                    ))}
                    {critical.length > 5 && (
                      <li className="text-muted-foreground">…y {critical.length - 5} más</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {validation.criticalCount === 0 && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-300/40 bg-emerald-50 p-3 text-xs text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200">
            <CheckCircle2 className="h-4 w-4" />
            Todo listo. Se generarán {archivos.length} fichero{archivos.length === 1 ? "" : "s"} SUENLACE
            + un Excel del Libro de IVA en un ZIP.
          </div>
        )}

        </div>

        <DialogFooter className="shrink-0 gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            <X className="mr-1 h-4 w-4" /> Cancelar
          </Button>
          <Button onClick={handleExport} disabled={blocked}>
            {isExporting ? "Generando…" : "Generar ZIP SUENLACE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportBatchesDialog;
